import type { ReactNode } from "react";
import { Text, View } from "react-native";

import { CardHero, CardHeroCaption, HERO_ART_WIDTH } from "@/components/brand/CardHero";
import { isRawCopy } from "@/components/browse/CardComparePrices";
import { Card } from "@/components/ui/Card";
import type { PublicItemDetail } from "@/hooks/usePublicBrowse";
import { formatGradeSeal } from "@/lib/catalog/cardIdentity";
import { formatCurrency } from "@/lib/money/money";

export type PublicCardSummaryProps = {
  detail: PublicItemDetail;
  /** The label beside a set price, such as "Asking price". Defaults to "Price". */
  priceLabel?: string;
  /** Prefix for the `-name`, `-identity` and `-price` testIDs, e.g. "browse-detail". */
  testIDPrefix: string;
  /** Rendered below the price card, such as the table's "Add to my list". */
  action?: ReactNode;
};

/**
 * The top of a public card page: the art, what the card is, its condition and copies, and the
 * vendor's price in the item's currency. Shared by a table's card detail
 * (`browse/[eventId]/[itemId]`) and a shop's card detail (`shop/[slug]/item/[itemId]`).
 */
export function PublicCardSummary(
  { detail, priceLabel = "Price", testIDPrefix, action }: PublicCardSummaryProps,
) {
  const { item } = detail;
  return (
    <View className="gap-3">
      <View className="items-center">
        <CardHero
          name={item.product_name}
          set={item.set_name}
          number={item.number}
          variant={item.variant_label}
          image={item.image_url}
          // Catalog rarity (#755); absent for a generic unit, which reads as unknown, never
          // "common". It drives the art border but is not printed (TRO-775).
          rarity={item.rarity}
          seal={formatGradeSeal(item.grading_company, item.grade, item.is_graded)}
          width={HERO_ART_WIDTH}
          caption="below"
        />
      </View>
      <CardHeroCaption
        name={item.product_name}
        set={item.set_name}
        number={item.number}
        variant={item.variant_label}
        testIDs={{ name: `${testIDPrefix}-name`, identity: `${testIDPrefix}-identity` }}
      />
      {isRawCopy(detail)
        ? (
          <Text className="text-center text-sm font-body text-muted-foreground">
            {`Condition: ${item.condition ?? "Not recorded"}`}
          </Text>
        )
        : null}
      {item.qty > 1
        ? (
          <Text className="text-center text-xs font-label text-muted-foreground">
            {item.qty} available
          </Text>
        )
        : null}
      {/* Without a price it reads "Ask vendor", as in the browse list (#469), never free. */}
      <Card className="flex-row items-center justify-between">
        <Text className="text-sm font-body text-muted-foreground">
          {item.price != null ? priceLabel : "Price"}
        </Text>
        {item.price != null
          ? (
            <Text testID={`${testIDPrefix}-price`} className="font-num-bold text-2xl text-primary">
              {formatCurrency(item.price, item.currency)}
            </Text>
          )
          : <Text className="text-base font-body text-muted-foreground">Ask vendor</Text>}
      </Card>
      {action}
    </View>
  );
}
